// Lecture « métier » d'une offre : ce que la page doit dire, sans rien recalculer
// de ce que l'API a figé (les prix et les dates viennent tels quels de l'offre).
import type { OffrePresentee } from '../../api/offres.js';
import type { GalerieSession } from '../../api/types.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';

export type SegmentOffres = 'aTraiter' | 'negociation' | 'acceptees' | 'closes' | 'toutes';
export const SEGMENTS_OFFRES: readonly SegmentOffres[] = [
  'aTraiter',
  'negociation',
  'acceptees',
  'closes',
  'toutes',
];
/** Les anciens liens (`?segment=aRelancer`, `?aRelancer=true`…) arrivent encore ici. */
const ANCIENS_SEGMENTS: Record<string, SegmentOffres> = {
  brouillons: 'aTraiter',
  aRelancer: 'aTraiter',
  envoyees: 'negociation',
};
export function segmentDepuisParametre(valeur: string | null): SegmentOffres {
  const direct = SEGMENTS_OFFRES.find((s) => s === valeur);
  if (direct) return direct;
  return (valeur ? ANCIENS_SEGMENTS[valeur] : undefined) ?? 'aTraiter';
}

/** « 2026-10-20 » dans le fuseau local : la date civile des échéances. */
export function dateCivile(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
/** Jours entiers de `de` à `a` (dates civiles) ; négatif si `a` est antérieur. */
export function joursEntre(de: string, a: string): number {
  const ms = (valeur: string) => {
    const [annee, mois, jour] = valeur.slice(0, 10).split('-').map(Number);
    return Date.UTC(annee!, mois! - 1, jour!);
  };
  return Math.round((ms(a) - ms(de)) / 86_400_000);
}

export interface EcartPrix {
  /** Somme des prix demandés (publics) des lignes qui en ont un. */
  demandeCentimes: number;
  /** Somme des prix proposés pour ces mêmes lignes. */
  proposeCentimes: number;
  /** Négatif : sous le prix demandé. */
  ecartCentimes: number;
  /** Écart rapporté au prix demandé, en pourcentage à une décimale (négatif : remise). */
  pourcent: number;
  /** Certaines lignes n'ont pas de prix public : l'écart ne porte que sur les autres. */
  partiel: boolean;
}
/** L'écart entre ce que la galerie propose et le prix demandé ; `null` sans prix public. */
export function ecartAuPrixDemande(
  lignes: readonly Pick<
    OffrePresentee['lignes'][number],
    'prixPublicCentimes' | 'prixProposeCentimes'
  >[],
): EcartPrix | null {
  const avecPrix = lignes.filter((l) => l.prixPublicCentimes !== null && l.prixPublicCentimes > 0);
  if (!avecPrix.length) return null;
  const demandeCentimes = avecPrix.reduce((s, l) => s + (l.prixPublicCentimes ?? 0), 0);
  const proposeCentimes = avecPrix.reduce((s, l) => s + l.prixProposeCentimes, 0);
  return {
    demandeCentimes,
    proposeCentimes,
    ecartCentimes: proposeCentimes - demandeCentimes,
    pourcent: Math.round(((proposeCentimes - demandeCentimes) / demandeCentimes) * 1000) / 10,
    partiel: avecPrix.length < lignes.length,
  };
}

export interface Echeance {
  /** Jours restants avant la fin de validité ; négatif une fois passée. */
  jours: number;
  /** `passee` : validité dépassée ; `proche` : dans la semaine ; `ouverte` sinon. */
  etat: 'passee' | 'proche' | 'ouverte';
}
export function echeanceDe(offre: OffrePresentee, maintenant = new Date()): Echeance {
  const jours = joursEntre(dateCivile(maintenant), offre.validiteJusquau);
  return { jours, etat: jours < 0 ? 'passee' : jours <= 7 ? 'proche' : 'ouverte' };
}

export type ProchaineAction =
  /** Brouillon : à envoyer (ou à remettre en main propre). */
  | 'envoyer'
  /** Envoyée, relance programmée arrivée à échéance. */
  | 'relancer'
  /** Envoyée, validité dépassée : relancer ou clore. */
  | 'echue'
  /** Envoyée : la réponse du contact est attendue. */
  | 'attendre'
  /** Acceptée : à transformer en vente (facture). */
  | 'convertir'
  /** Acceptée sans facturation : œuvres à réserver. */
  | 'reserver'
  /** Plus rien à faire. */
  | 'rien';

export interface ContexteAction {
  /** La galerie a le module facturation et le droit de créer une facture. */
  peutFacturer: boolean;
  /** La galerie peut créer des réservations. */
  peutReserver: boolean;
}
export function prochaineAction(
  offre: OffrePresentee,
  contexte: ContexteAction,
  maintenant = new Date(),
): ProchaineAction {
  switch (offre.etat) {
    case 'brouillon':
      return 'envoyer';
    case 'envoyee': {
      if (offre.echue) return 'echue';
      const jour = dateCivile(maintenant);
      if (offre.relancePrevueLe && offre.relancePrevueLe.slice(0, 10) <= jour) return 'relancer';
      return 'attendre';
    }
    case 'acceptee':
      if (contexte.peutFacturer && !offre.factureId) return 'convertir';
      if (contexte.peutReserver && offre.reservationIds.length < offre.lignes.length)
        return 'reserver';
      return 'rien';
    default:
      return 'rien';
  }
}
/** Une offre « à traiter » attend un geste de la galerie, pas une réponse du contact. */
export function aTraiter(offre: OffrePresentee, contexte: ContexteAction, maintenant = new Date()) {
  const action = prochaineAction(offre, contexte, maintenant);
  return action !== 'attendre' && action !== 'rien';
}

export type EvenementFrise =
  | { cle: 'creee'; date: string }
  | { cle: 'envoi' | 'relance'; date: string; destinataire: string; resultat: string }
  | { cle: 'envoyee'; date: string; canal: OffrePresentee['canal'] }
  | { cle: 'relancePrevue'; date: string; due: boolean }
  | { cle: 'echeance'; date: string; passee: boolean }
  | { cle: 'acceptee' | 'refusee' | 'retiree' | 'expiree'; date: string; motif: string }
  | { cle: 'reservation'; fait: boolean; reservees: number; total: number }
  | { cle: 'facture'; fait: boolean };

const instant = (valeur: string) =>
  valeur.length <= 10 ? Date.parse(`${valeur}T12:00:00Z`) : Date.parse(valeur);

/**
 * La négociation dans l'ordre : ce qui s'est passé (dates croissantes), puis ce
 * qui reste à venir. Une offre n'a pas de contre-proposition dans le modèle :
 * la frise suit les envois, relances et la réponse finale.
 */
export function friseDeLOffre(
  offre: OffrePresentee,
  maintenant = new Date(),
): { passes: EvenementFrise[]; aVenir: EvenementFrise[] } {
  const jour = dateCivile(maintenant);
  const passes: EvenementFrise[] = [{ cle: 'creee', date: offre.creeeLe }];
  const aVenir: EvenementFrise[] = [];
  const envoisInitiaux = offre.envois.filter((e) => e.type === 'envoi');
  if (offre.envoyeeLe && !envoisInitiaux.length)
    passes.push({ cle: 'envoyee', date: offre.envoyeeLe, canal: offre.canal });
  for (const envoi of offre.envois)
    passes.push({
      cle: envoi.type,
      date: envoi.le,
      destinataire: envoi.destinataire,
      resultat: envoi.resultat,
    });
  if (offre.accepteeLe)
    passes.push({ cle: 'acceptee', date: offre.accepteeLe, motif: offre.motif });
  if (offre.refuseeLe) passes.push({ cle: 'refusee', date: offre.refuseeLe, motif: offre.motif });
  if (offre.retireeLe) passes.push({ cle: 'retiree', date: offre.retireeLe, motif: offre.motif });
  if (offre.expireeLe) passes.push({ cle: 'expiree', date: offre.expireeLe, motif: '' });
  passes.sort(
    (a, b) => instant((a as { date: string }).date) - instant((b as { date: string }).date),
  );

  if (offre.etat === 'envoyee') {
    if (offre.relancePrevueLe)
      aVenir.push({
        cle: 'relancePrevue',
        date: offre.relancePrevueLe,
        due: offre.relancePrevueLe.slice(0, 10) <= jour,
      });
    aVenir.push({
      cle: 'echeance',
      date: offre.validiteJusquau,
      passee: offre.validiteJusquau < jour,
    });
  } else if (offre.etat === 'brouillon') {
    aVenir.push({
      cle: 'echeance',
      date: offre.validiteJusquau,
      passee: offre.validiteJusquau < jour,
    });
  } else if (offre.etat === 'acceptee') {
    aVenir.push({
      cle: 'reservation',
      fait: offre.reservationIds.length >= offre.lignes.length,
      reservees: offre.reservationIds.length,
      total: offre.lignes.length,
    });
    aVenir.push({ cle: 'facture', fait: Boolean(offre.factureId) });
  }
  return { passes, aVenir };
}

/** Ce que la session peut enchaîner après une offre acceptée (même règle que la fiche). */
export function contexteActionDeLaGalerie(galerie: GalerieSession): ContexteAction {
  const peutEcrire = galerieALaction(galerie, 'offers:write');
  return {
    peutFacturer:
      peutEcrire &&
      galerieAModule(galerie, 'facturation') &&
      galerieALaction(galerie, 'invoices:write'),
    peutReserver: peutEcrire && galerieALaction(galerie, 'reservations:write'),
  };
}
